import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
// Slash command support
import SlashCommandAutocomplete from './SlashCommandAutocomplete';
import { useSlashCommands } from '../hooks/useSlashCommands';
import { PromptTemplate } from '../services/promptTemplateService';
import { copyFormattedContent } from '../utils/copyFormatted';
import { markdownTableComponents } from './TableExportWrapper';
// Simplified ChatInterface without citation support



// Utility function to convert markdown to plain text (removes ALL markdown formatting)
function markdownToFormattedText(markdown: string): string {
  console.log('🔧 FORMATTING DEBUG - Input markdown:', markdown.substring(0, 300));
  
  let formattedText = markdown;
  
  // Step 1: Preserve code blocks temporarily to avoid processing their content
  const codeBlocks: string[] = [];
  formattedText = formattedText.replace(/```[\s\S]*?```/g, (match: string) => {
    const code = match.replace(/```\w*\n?/g, '').replace(/```$/g, '').trim();
    codeBlocks.push(code);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });
  
  // Step 2: Preserve inline code temporarily
  const inlineCode: string[] = [];
  formattedText = formattedText.replace(/`([^`]+)`/g, (match: string, code: string) => {
    inlineCode.push(code);
    return `__INLINE_CODE_${inlineCode.length - 1}__`;
  });
  
  // Step 3: Remove ALL markdown formatting
  
  // Remove citations
  formattedText = formattedText.replace(/\[CITE:\s*"[^"]*"\]/g, '');
  formattedText = formattedText.replace(/\[CITATION[^\]]*\]/g, '');
  
  // Headers - just keep the text, add some spacing
  formattedText = formattedText.replace(/^#{1,6}\s+(.+)$/gm, '\n$1\n');
  
  // Bold and italic - remove all variations
  formattedText = formattedText.replace(/\*\*\*(.+?)\*\*\*/g, '$1'); // Bold italic
  formattedText = formattedText.replace(/___(.+?)___/g, '$1'); // Bold italic alt
  formattedText = formattedText.replace(/\*\*(.+?)\*\*/g, '$1'); // Bold
  formattedText = formattedText.replace(/__(.+?)__/g, '$1'); // Bold alt
  formattedText = formattedText.replace(/\*(.+?)\*/g, '$1'); // Italic
  formattedText = formattedText.replace(/_(.+?)_/g, '$1'); // Italic alt
  
  // Strikethrough
  formattedText = formattedText.replace(/~~(.+?)~~/g, '$1');
  formattedText = formattedText.replace(/~(.+?)~/g, '$1');
  
  // Links - just keep the text
  formattedText = formattedText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  formattedText = formattedText.replace(/\[([^\]]+)\]\[[^\]]+\]/g, '$1');
  
  // Images - just show alt text
  formattedText = formattedText.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
  
  // Reference-style links
  formattedText = formattedText.replace(/^\[[^\]]+\]:\s+.+$/gm, '');
  
  // Lists - convert to simple bullets
  formattedText = formattedText.replace(/^\s*[\*\-\+]\s+(.*)$/gm, '• $1');
  formattedText = formattedText.replace(/^\s*\d+\.\s+(.*)$/gm, '• $1');
  
  // Task lists
  formattedText = formattedText.replace(/^\s*[\*\-\+]\s+\[[ xX]\]\s+(.*)$/gm, '• $1');
  
  // Tables - simplify
  const lines = formattedText.split('\n');
  const processedLines: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.match(/^\|.*\|$/)) {
      // Table row
      if (line.match(/^[\s\|\-\:]+$/)) {
        // Skip separator lines
        continue;
      }
      const cells = line.split('|')
        .slice(1, -1)
        .map((cell: string) => cell.trim());
      processedLines.push(cells.join(' | '));
    } else {
      processedLines.push(line);
    }
  }
  formattedText = processedLines.join('\n');
  
  // Blockquotes
  formattedText = formattedText.replace(/^>\s*(.*)$/gm, '$1');
  formattedText = formattedText.replace(/^>+\s*(.*)$/gm, '$1'); // Nested quotes
  
  // Horizontal rules
  formattedText = formattedText.replace(/^[\*\-_]{3,}$/gm, '―――――――――――――――――――――――');
  
  // HTML tags (basic ones that might appear)
  formattedText = formattedText.replace(/<br\s*\/?>/gi, '\n');
  formattedText = formattedText.replace(/<\/?(p|div|span|strong|em|b|i|u|h[1-6])[^>]*>/gi, '');
  formattedText = formattedText.replace(/<[^>]+>/g, ''); // Remove any other HTML tags
  
  // Step 4: Restore code blocks and inline code (without formatting)
  formattedText = formattedText.replace(/__CODE_BLOCK_(\d+)__/g, (match: string, index: string) => {
    return '\n' + codeBlocks[parseInt(index)] + '\n';
  });
  
  formattedText = formattedText.replace(/__INLINE_CODE_(\d+)__/g, (match: string, index: string) => {
    return inlineCode[parseInt(index)];
  });
  
  // Step 5: Clean up whitespace and formatting
  formattedText = formattedText.replace(/\n{3,}/g, '\n\n'); // Max two newlines
  formattedText = formattedText.replace(/^\n+/, ''); // Remove leading newlines
  formattedText = formattedText.replace(/\n+$/, ''); // Remove trailing newlines
  formattedText = formattedText.replace(/[ \t]+$/gm, ''); // Remove trailing spaces on lines
  formattedText = formattedText.trim();
  
  console.log('🎯 FORMATTING RESULT:', formattedText.substring(0, 300));
  console.log('🔍 Has ** remaining:', formattedText.includes('**'));
  console.log('🔍 Has __ remaining:', formattedText.includes('__'));
  console.log('🔍 Has ``` remaining:', formattedText.includes('```'));
  console.log('🔍 Has [CITE remaining:', formattedText.includes('[CITE'));
  
  return formattedText;
}

// Simplified content renderer - just renders markdown
function renderContent(text: string): React.ReactNode {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight, rehypeKatex]}
      components={markdownTableComponents}
    >
      {text}
    </ReactMarkdown>
  );
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
}

interface ChatInterfaceProps {
  onSendMessage: (message: string, displayMessage?: string) => void;
  messages: ChatMessage[];
  isLoading?: boolean;
  className?: string;
  documents?: Array<{id: string, name: string}>;
  onClearChat?: () => void;
  saveStatus?: 'saved' | 'saving' | 'error';
  sessionTitle?: string | null;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  onSendMessage,
  messages,
  isLoading = false,
  className = '',
  documents,
  onClearChat,
  saveStatus = 'saved',
  sessionTitle
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const [isOverlayMode, setIsOverlayMode] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  // Drawer width when expanded (px). User can drag the left edge to resize
  // between 400 and 60% of the viewport.
  const [drawerWidth, setDrawerWidth] = useState<number>(640);
  const isResizingRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Slash command functionality
  const {
    isAutocompleteVisible,
    searchQuery,
    autocompletePosition,
    handleInputChange,
    handleTemplateSelect,
    closeAutocomplete,
    handleKeyDown: handleSlashKeyDown,
    setInputRef,
    getCurrentPromptForMessage
  } = useSlashCommands({
    onTemplateSelect: (template: PromptTemplate, displayText: string, actualPrompt: string) => {
      console.log('=== TEMPLATE SELECTED (ChatInterface) ===');
      console.log('Template:', template.title);
      console.log('Display text:', displayText);
      console.log('Actual prompt:', actualPrompt.substring(0, 100) + '...');
      console.log('=======================================');
      // Note: We don't set input message here anymore, we use the mapping system
    }
  });

  // Set input ref for slash commands
  useEffect(() => {
    if (inputRef.current) {
      setInputRef(inputRef.current);
    }
  }, [setInputRef]);

  // Auto-scroll to bottom when new messages arrive, but keep chat in view
  useEffect(() => {
    if (messagesEndRef.current && !isOverlayMode) {
      // Scroll the chat container, not the entire page
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [messages, isOverlayMode]);


  // Handle overlay mode
  const toggleOverlayMode = () => {
    setIsOverlayMode(!isOverlayMode);
  };

  // Handle click outside overlay to close
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOverlayMode(false);
    }
  };

  // Removed citation handling functionality


  // Calculate dynamic height based on content - taller aspect ratio
  const calculateChatHeight = () => {
    if (isOverlayMode) return 'h-screen';

    const messageCount = messages.length;
    if (messageCount === 0) return 'h-[500px]';
    if (messageCount <= 3) return 'h-[600px]';
    if (messageCount <= 6) return 'h-[700px]';
    if (messageCount <= 10) return 'h-[800px]';
    return 'h-[900px]';
  };

  // Drag-to-resize: while the user is dragging the drawer's left edge, set
  // the new width based on the cursor X (clamped between 400 and 60vw).
  useEffect(() => {
    if (!isOverlayMode) return;
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const maxW = Math.round(window.innerWidth * 0.6);
      const next = Math.max(400, Math.min(maxW, window.innerWidth - e.clientX));
      setDrawerWidth(next);
    };
    const onUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isOverlayMode]);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle clear chat
  const handleClearChat = () => {
    if (onClearChat && messages.length > 0) {
      if (window.confirm('Are you sure you want to clear this chat? This action cannot be undone.')) {
        onClearChat();
      }
    }
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() && !isLoading) {
      const displayMessage = inputMessage.trim(); // What to show in chat
      const messageToSend = getCurrentPromptForMessage(inputMessage); // What to send to backend
      
      console.log('=== SEND MESSAGE DEBUG (ChatInterface) ===');
      console.log('Display message (for chat):', displayMessage);
      console.log('Backend message (actual):', messageToSend.substring(0, 100) + '...');
      console.log('Is template?', messageToSend !== displayMessage);
      console.log('=========================================');
      
      // Check if this is a template expansion
      if (messageToSend !== displayMessage) {
        // Template case: pass both display and backend messages
        onSendMessage(messageToSend, displayMessage);
      } else {
        // Regular message: just pass the message
        onSendMessage(messageToSend);
      }
      
      setInputMessage('');
      
      // Focus back to input after sending
      setTimeout(() => {
      inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Copy formatted text to clipboard with HTML formatting preserved
  const copyToClipboard = async (text: string, messageId: string) => {
    // Find the rendered message element
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;

    if (messageElement) {
      const success = await copyFormattedContent(text, messageElement);
      if (success) {
        setCopiedMessageId(messageId);
        // Clear the "Copied!" feedback after 2 seconds
        setTimeout(() => setCopiedMessageId(null), 2000);
      }
    } else {
      // Fallback to plain text copy if element not found
      try {
        const formattedText = markdownToFormattedText(text);
        await navigator.clipboard.writeText(formattedText);
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    }
  };

  const chatContent = (
    <div
      ref={chatContainerRef}
      style={isOverlayMode ? { width: `${drawerWidth}px` } : undefined}
      className={`flex flex-col bg-white dark:bg-dark-surface ${
        isOverlayMode ? '' : 'rounded-xl'
      } shadow-lg border ${isOverlayMode ? 'border-l border-gray-200 dark:border-dark-border' : 'border-gray-200 dark:border-dark-border'} canvas-like-chat ${calculateChatHeight()} ${
        isOverlayMode ? '' : 'w-full'
      }`}
    >
      {/* Header with overlay toggle and clear buttons */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-sm font-medium text-secondary dark:text-dark-text">
            {sessionTitle || 'Risk Ninja AI'}
          </span>
          {messages.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </span>
          )}
          {/* Save Status Indicator */}
          {messages.length > 0 && (
            <div className="flex items-center gap-1 text-xs">
              {saveStatus === 'saving' && (
                <>
                  <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-blue-500">Saving...</span>
                </>
              )}
              {saveStatus === 'saved' && (
                <>
                  <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-500">Saved</span>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-500">Save failed</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Clear Chat Button */}
          {onClearChat && messages.length > 0 && (
            <button
              onClick={handleClearChat}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Clear chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
              </svg>
            </button>
          )}
          
          {/* Expand/Minimize Button */}
          <button
            onClick={toggleOverlayMode}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={isOverlayMode ? "Minimize chat" : "Expand chat"}
          >
            {isOverlayMode ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
            )}
          </button>
        </div>
      </div>


      {/* Messages Area - Gemini Canvas Style */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-8 space-y-8 bg-gradient-to-br from-slate-50/80 via-white to-slate-50/60 dark:from-slate-900/80 dark:via-slate-800 dark:to-slate-900/60 backdrop-blur-sm chat-messages-container"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="text-primary">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 8H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M17 12H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-secondary dark:text-dark-text mb-3">
              Welcome to Risk Ninja AI
            </h3>
            <p className="text-accent dark:text-dark-muted text-lg max-w-2xl leading-relaxed">
              {user 
                ? 'I\'m here to help you analyze policies, assess risks, and optimize your insurance coverage. Upload documents or ask me anything about commercial insurance.'
                : 'Sign in to unlock powerful AI-driven insurance analysis and risk assessment tools.'
              }
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in duration-500`}
              >
                <div
                  className={`max-w-4xl rounded-2xl px-6 py-4 shadow-sm ${
                    message.sender === 'user'
                      ? 'chat-message-user'
                      : 'chat-message-ai border border-gray-200/50 dark:border-gray-700/50'
                  }`}
                >
                  {message.sender === 'ai' && (
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-200/30 dark:border-gray-700/30">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-primary dark:text-blue-400">Risk Ninja AI</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`text-base leading-relaxed ${message.sender === 'user' ? 'text-white' : 'text-gray-800 dark:text-gray-100'}`}>
                    <div
                      data-message-id={message.id}
                      className="overflow-x-auto markdown-content prose prose-lg max-w-none dark:prose-invert"
                    >
                      {renderContent(message.content)}
                    </div>
                    {message.isStreaming && (
                      <span className="inline-block w-1 h-6 bg-current ml-2 animate-pulse rounded-full"></span>
                    )}
                  </div>
                  {message.sender === 'user' && (
                    <div className="flex items-center justify-end mt-2 text-xs text-blue-100">
                    <span>{formatTime(message.timestamp)}</span>
                    </div>
                  )}
                    {message.sender === 'ai' && !message.isStreaming && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200/30 dark:border-gray-700/30">
                      <div className="flex items-center gap-2">
                        {/* Copy button will be here */}
                      </div>
                      <button
                        onClick={() => copyToClipboard(message.content, message.id)}
                        className={`flex items-center gap-1 px-3 py-1 text-xs transition-all rounded-lg ${
                          copiedMessageId === message.id
                            ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50'
                            : 'text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        }`}
                        title="Copy as formatted text"
                      >
                        {copiedMessageId === message.id ? (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area - Enhanced Gemini Style */}
      <div className="p-6 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm border-t border-gray-200/50 dark:border-gray-700/50 rounded-b-xl">
        {user ? (
          <>
            {/* Suggestion chips — above the input, only on empty state.
                Once the conversation has started, the chips disappear so they
                stop taking up space the user wants for messages. */}
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 mb-4 max-w-3xl mx-auto justify-center">
                <button
                  onClick={() => setInputMessage("Compare coverage limits across my policies")}
                  className="px-3 py-1.5 text-xs bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all"
                >
                  💼 Compare Coverage
                </button>
                <button
                  onClick={() => setInputMessage("Identify potential risk exposures in my business")}
                  className="px-3 py-1.5 text-xs bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all"
                >
                  ⚠️ Risk Assessment
                </button>
                <button
                  onClick={() => setInputMessage("Find opportunities to reduce insurance costs")}
                  className="px-3 py-1.5 text-xs bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all"
                >
                  💰 Cost Optimization
                </button>
                <button
                  onClick={() => setInputMessage("Analyze my premium trends and projections")}
                  className="px-3 py-1.5 text-xs bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all"
                >
                  📊 Premium Analysis
                </button>
              </div>
            )}

            <div className="flex items-end gap-3 max-w-3xl mx-auto">
              <div className="flex-1">
                <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => {
                    setInputMessage(e.target.value);
                    handleInputChange(e.target.value);
                  }}
                  onKeyDown={(e) => handleSlashKeyDown(e, inputMessage)}
                  onKeyPress={handleKeyPress}
                    placeholder="Ask about policies, risks, coverage gaps, or premium optimization..."
                  disabled={isLoading}
                    className="w-full px-5 py-3 bg-white dark:bg-gray-800 border border-gray-300/50 dark:border-gray-600/50 rounded-2xl text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all duration-200 disabled:opacity-50 shadow-sm text-sm"
                />
                
                {/* Slash Command Autocomplete - attached to input */}
                <SlashCommandAutocomplete
                  isVisible={isAutocompleteVisible}
                  searchQuery={searchQuery}
                  onSelect={(template) => handleTemplateSelect(template, inputMessage, setInputMessage)}
                  onClose={closeAutocomplete}
                  position={autocompletePosition}
                />
                  {isLoading && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 dark:disabled:from-gray-600 dark:disabled:to-gray-700 text-white rounded-2xl font-medium transition-all duration-200 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22,2 15,22 11,13 2,9"></polygon>
                    </svg>
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Slash command hint */}
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
              Type "/" to use prompt templates • ⌘K for template management
            </div>
          </>
        ) : (
          <div className="text-center py-8 max-w-md mx-auto">
            <div className="mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              </div>
              <h4 className="text-lg font-semibold text-secondary dark:text-dark-text mb-2">
                Sign in to get started
              </h4>
              <p className="text-sm text-accent dark:text-dark-muted leading-relaxed">
                Unlock AI-powered insurance analysis, risk assessment, and policy optimization tools.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isOverlayMode) {
    return (
      <>
        {/* Subtle backdrop — lets the report show through so the user can
            still read what they're asking about. Click closes the drawer. */}
        <div
          className="fixed inset-0 z-40 bg-black/10"
          onClick={() => setIsOverlayMode(false)}
        />
        {/* Right-side drawer */}
        <div
          className="fixed right-0 top-0 z-50 h-screen flex items-stretch animate-in slide-in-from-right duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle on the LEFT edge of the drawer */}
          <div
            onMouseDown={startResize}
            title="Drag to resize"
            className="w-1.5 cursor-col-resize bg-transparent hover:bg-blue-400/40 transition-colors"
          />
          {chatContent}
        </div>
      </>
    );
  }

  return (
    <div className={className}>
      {chatContent}
    </div>
  );
};

export default ChatInterface; 